import { useState, useEffect } from 'react';
import { Flex, Form, Upload, message } from 'antd';
import TextField from '../form/text-field/text-field';
import { TextArea } from '../form/textarea/textarea';
import Dropdown from '../form/dropdown/dropdown';
import { Button } from '../button/button';
import { useSupport } from '@/hooks/use-support/use-support';
import { useUser } from '@/hooks/use-user/use-user';
import styles from './support.module.scss';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export type SupportFormFieldType = {
  subject: string;
  message: string;
  category?: string;
};

export function Support() {
  const { t } = useTranslation(['support', 'auth', 'common']);
  const [form] = Form.useForm();
  const { userData } = useUser();
  const {
    submitSupportTicket,
    isSubmittingTicket,
    isTicketSubmitted,
    isTicketError,
    ticketError,
    ticketData,
    resetTicket,
  } = useSupport();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (isTicketSubmitted && ticketData) {
      messageApi.open({
        type: 'success',
        content: t('support:form.success', { ticketNumber: ticketData.ticket_number }),
      });
      form.resetFields();
      setFileList([]);
      resetTicket();
    }
  }, [isTicketSubmitted, ticketData, form, resetTicket, messageApi, t]);

  const categoryOptions = [
    { label: t('support:form.categories.common'), value: 'common' },
    { label: t('support:form.categories.removeAccount'), value: 'remove_account' },
    { label: t('support:form.categories.removeData'), value: 'remove_data' },
  ];

  const handleSubmit = (values: SupportFormFieldType) => {
    const files = fileList.map((file) => file.originFileObj as File).filter(Boolean);

    const tags = values.category ? [values.category] : undefined;

    submitSupportTicket({
      support_ticket: {
        subject: values.subject,
        message: values.message,
        tags,
      },
      attachments: files.length > 0 ? files : undefined,
    });
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList];
    newFileList = newFileList.slice(-1);
    setFileList(newFileList);
  };

  const handleReset = () => {
    form.resetFields();
    setFileList([]);
    resetTicket();
  };

  const getErrorMessage = () => {
    if (ticketError && ticketError instanceof AxiosError) {
      const errorData = ticketError.response?.data;
      if (errorData && typeof errorData === 'object' && 'error' in errorData) {
        return String(errorData.error);
      }
      return ticketError.message || t('support:form.submitFailed');
    }
    return t('support:form.submitFailed');
  };

  return (
    <Form form={form} layout="vertical" className={styles.formContainer} onFinish={handleSubmit}>
      {contextHolder}
      <Flex vertical gap={16}>
        <div className={styles.userInfo}>
          <p>
            <strong>{t('support:userInfo.name')}</strong> {userData?.first_name}{' '}
            {userData?.last_name}
          </p>
          <p>
            <strong>{t('support:userInfo.email')}</strong> {userData?.email}
          </p>
        </div>

        <Form.Item<SupportFormFieldType>
          label={t('support:form.subject')}
          name="subject"
          rules={[{ required: true, message: t('support:form.subjectRequired') }]}
        >
          <TextField placeholder={t('support:form.subjectPlaceholder')} />
        </Form.Item>

        <Form.Item<SupportFormFieldType>
          label={t('support:form.message')}
          name="message"
          rules={[{ required: true, message: t('support:form.messageRequired') }]}
        >
          <TextArea placeholder={t('support:form.messagePlaceholder')} rows={6} />
        </Form.Item>

        <Form.Item<SupportFormFieldType> label={t('support:form.category')} name="category">
          <Dropdown placeholder={t('support:form.categoryPlaceholder')} options={categoryOptions} />
        </Form.Item>

        <Form.Item label={t('support:form.attachment')}>
          <Upload
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
            accept="image/*"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />} buttonVariant="secondary">
              {t('support:form.uploadImage')}
            </Button>
          </Upload>
        </Form.Item>

        {isTicketError && <div className={styles.errorMessage}>{getErrorMessage()}</div>}

        <Form.Item>
          <Flex gap={10}>
            <Button type="primary" htmlType="submit" loading={isSubmittingTicket}>
              {t('support:form.submit')}
            </Button>
            <Button buttonVariant="secondary" onClick={handleReset} disabled={isSubmittingTicket}>
              {t('common:actions.clear')}
            </Button>
          </Flex>
        </Form.Item>
      </Flex>
    </Form>
  );
}
